<template>
	<div ref="rootElement">
		<div class="jw-scoring-listing" :class="{ 'poster-details': isPosterDetail }">
			<div class="jw-scoring-listing__rating" v-for="score of filteredScoring" :key="score.queryName">
				<div
					class="jw-scoring-listing__rating--group"
					:class="{
						'jw-scoring-listing__rating--no-link': !score.link,
						'jw-scoring-listing__rating--link': score.link,
					}"
					@click="onRatingClick($event, score)"
				>
					<img
						:class="{ 'is-imdb': score.provider === 'imdb' }"
						:loading="lazy ? 'lazy' : 'eager'"
						:alt="score.name"
						:src="score.image"
						:tooltip="score.name"
					/>

					<div v-if="score.name === 'JustWatch Rating'">
						{{ score.score }}{{ score.append }}
						<span v-if="showJwVotes"> {{ formatVotes(computedJwVotes) }}</span>
					</div>

					<span v-else-if="score.name === 'ROTTEN TOMATOES'">{{ score.score }}{{ score.append }}</span>

					<div v-else class="nowrap">{{ score.score }} {{ score.append }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { ExternalIds, Scoring } from '@/@types/graphql-types';

import { numberToCompactString } from '@/helpers/NumberFormat';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getScorings, getVotesLabel, ScoringProvider, getRottenTomatoesScoreIcon } from '@/helpers/scoring-helper';

import { computed } from 'vue';

const props = withDefaults(
	defineProps<{
		scores: Scoring;
		externalIds?: ExternalIds;
		excluded?: ScoringProvider[];
		includeLinks?: boolean;
		imdbOnly?: boolean;
		lazy?: boolean;
		showVotes?: boolean;
		additionalContexts?: SnowplowContext[];
		jwVotes?: number | null;
		showJwVotes?: boolean;
		isPosterDetail?: boolean;
		excludeRottenTomatoes?: boolean;
	}>(),
	{
		externalIds: () => ({}),
		excluded: () => [],
		includeLinks: true,
		lazy: true,
		additionalContexts: () => [],
		jwVotes: null,
		showJwVotes: true,
	}
);

const route = useRoute();

const computedJwVotes = computed(() => (props.jwVotes === null ? 0 : props.jwVotes));

const scoring = computed(() => {
	const scoring = getScorings(props.scores, props.externalIds, props.excluded, props.includeLinks);

	return scoring.map(score => {
		if (score.provider === ScoringProvider.IMDB) {
			const votes = props.scores.imdbVotes || 0;
			const votesLabel = getVotesLabel(votes);

			if (votesLabel && props.showVotes) {
				score.append = ` (${votesLabel})`;
			}
		}

		if (score.provider === ScoringProvider.TMDB && computedJwVotes.value > 30) {
			const calculatedScore = Math.round((props.scores.jwRating ?? 0) * 100);
			return {
				append: '%',
				decimals: 0,
				filterDisplay: false,
				image: '/appassets/img/jw-icon.png',
				max: 100,
				min: 0,
				name: 'JustWatch Rating',
				provider: ScoringProvider.JUSTWATCH,
				queryName: 'justwatchRating',
				score: calculatedScore <= 0 ? '0' : calculatedScore,
				step: 1,
				value: 0,
				link: '',
			};
		}

		if (score.provider === ScoringProvider.ROTTEN_TOMATOES) {
			score.image = getRottenTomatoesScoreIcon(Number(score.score), !!props.scores.certifiedFresh);
		}

		return score;
	});
});

const filteredScoring = computed(() => {
	const filtered = scoring.value.filter(score => !!score.score && showRating(score));

	if (props.imdbOnly) {
		return filtered.filter(scoring => scoring.provider === ScoringProvider.IMDB);
	}

	if (props.excludeRottenTomatoes) {
		return filtered.filter(scoring => scoring.provider !== ScoringProvider.ROTTEN_TOMATOES);
	}

	return filtered;
});

function showRating(score: any) {
	if (score.name === 'JustWatch Rating' && computedJwVotes.value > 30) return true;
	return score.name !== 'JustWatch Rating';
}

function formatVotes(votes: number) {
	if (!votes || votes < 30) return '';
	if (votes >= 10000) return `(${numberToCompactString(votes, 0)})`;
	return `(${numberToCompactString(votes, 1)})`;
}

function onRatingClick($event: Event, score: any) {
	$event.preventDefault();
	$event.stopPropagation();
	$event.stopImmediatePropagation();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'rating_click',
			label: score.name,
		},
		props.additionalContexts
	);

	let scoreLink = score.link;

	// Reconstruct IMDb link to use current IMDb website URL formatting
	if (route.name === 'app.detail.show.season' && score.name === 'IMDB') {
		const imdbId = props.externalIds?.imdbId;
		let idInPath = imdbId;

		if (imdbId?.includes(':')) {
			const [id] = imdbId.split(':', 2);
			idInPath = id;
		}

		scoreLink = `https://www.imdb.com/title/${idInPath}?ref_=ref_ext_justwatch`;
	}

	window.open(scoreLink);
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

.jw-scoring-listing {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 10px;

	&__rating {
		img {
			width: 16px;
			margin-inline-end: 3px;
			vertical-align: sub;

			&.is-imdb {
				width: 24px;
			}
		}

		&--group {
			display: flex;
			align-items: center;
			gap: 4px;
		}

		&--link {
			color: $jw-color-lightblue;
			cursor: pointer;
		}

		&--no-link {
			color: inherit !important;
			pointer-events: none;
		}

		a {
			display: inline-flex;
			align-items: center;

			img {
				margin-inline-end: 6px;
			}
		}
	}

	&.poster-details {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-template-rows: auto auto;
		margin: auto;

		.jw-scoring-listing__rating {
			&:first-child {
				grid-column: span 2;
			}
		}
	}
}

@media (max-width: $screen-xxs) {
	.jw-scoring-listing {
		&__rating {
			font-size: 13px;

			&--img {
				margin-inline-end: 1px;
			}
		}
	}
}

.nowrap {
	white-space: nowrap;
}
</style>
